/**
 * turnstile.ts — Cloudflare Turnstile (xác minh người thật) phía trình duyệt: nạp script một lần + các hàm thuần.
 * Widget / hook: components/shared/turnstile-box.tsx. Backend: ecdp_backend_source/src/shared/turnstile.ts.
 *
 * Khoá công khai (site key) KHÔNG nằm trong bản build — lấy từ GET /auth/signup/config (`turnstileSiteKey`; null = chưa bật → không
 * vẽ widget, không gửi token). Token dùng được MỘT lần và sống 300 giây: sau mỗi lần gửi (kể cả bị từ chối) phải `reset()` lấy token mới.
 */
import { i18n } from '@/lib/i18n'

/** Mỗi form một action — PHẢI khớp `TurnstileAction` ở backend (token của form này không dùng được ở form khác). */
export type TurnstileAction = 'login' | 'platform-login' | 'register' | 'forgot-password' | 'affiliate-lead'

export const TURNSTILE_SCRIPT_SRC = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit'

export interface TurnstileRenderOptions {
  sitekey: string
  action?: string
  theme?: 'auto' | 'light' | 'dark'
  size?: 'normal' | 'flexible' | 'compact'
  language?: string
  callback?: (token: string) => void
  'expired-callback'?: () => void
  'timeout-callback'?: () => void
  /** Trả `true` để Cloudflare không in lỗi ra console (mình đã tự xử lý bằng thông báo trên form). */
  'error-callback'?: (errorCode?: string) => boolean | void
}

export interface TurnstileApi {
  render: (container: HTMLElement, options: TurnstileRenderOptions) => string
  reset: (widgetId: string) => void
  remove: (widgetId: string) => void
}

declare global {
  interface Window {
    turnstile?: TurnstileApi
  }
}

let loader: Promise<TurnstileApi> | null = null

/** Nạp api.js một lần (không tự lưu bản sao — Cloudflare yêu cầu tải thẳng từ challenges.cloudflare.com). Lỗi mạng → thử nạp lại được. */
export function loadTurnstile(): Promise<TurnstileApi> {
  if (typeof window === 'undefined') return Promise.reject(new Error('turnstile: không có window'))
  if (window.turnstile) return Promise.resolve(window.turnstile)
  if (!loader) {
    loader = new Promise<TurnstileApi>((resolve, reject) => {
      const s = document.createElement('script')
      s.src = TURNSTILE_SCRIPT_SRC
      s.async = true
      s.defer = true
      s.onload = () => (window.turnstile ? resolve(window.turnstile) : reject(new Error('turnstile')))
      s.onerror = () => {
        loader = null
        s.remove()
        reject(new Error('turnstile'))
      }
      document.head.appendChild(s)
    })
  }
  return loader
}

/** Backend từ chối vì CAPTCHA (CAPTCHA_REQUIRED / CAPTCHA_FAILED) → form phải lấy token mới. */
export const isCaptchaCode = (code: unknown): boolean => typeof code === 'string' && code.startsWith('CAPTCHA_')

/** Còn phải chờ xác minh? (đã bật Turnstile mà chưa có token) — nút gửi bị khoá. */
export const captchaPending = (siteKey: string | null | undefined, token: string | null): boolean => !!siteKey && !token

export function captchaPendingMessage(): string {
  return i18n.t('sharedMisc:turnstile.captchaPending')
}

/**
 * Vì sao ô xác minh chưa dùng được:
 *  - `script`: không tải được api.js từ challenges.cloudflare.com (mạng, trình chặn quảng cáo, tường lửa);
 *  - `widget`: script đã tải, widget báo lỗi qua `error-callback` (kèm mã lỗi của Cloudflare, vd "600010").
 *    Widget TỰ thử lại sau lỗi (`retry: auto`) nên đây chưa chắc là lỗi cuối cùng — thông báo mất khi xác minh xong;
 *  - `render`: script đã tải nhưng `render()` ném lỗi (khoá công khai cấu hình sai…) — widget KHÔNG được dựng nên không có gì tự thử lại.
 */
export interface TurnstileFailure {
  kind: 'script' | 'widget' | 'render'
  /** Mã lỗi Cloudflare đã làm sạch; null = không có / không hợp lệ. */
  code: string | null
  /** Chỉ cho `render`: nội dung lỗi `render()` ném ra (đã cắt ngắn) để ghi console; không hiện cho người dùng. */
  detail?: string | null
}

/** Nội dung lỗi bất kỳ → một dòng ngắn (≤ 160 ký tự) để ghi console. */
export function turnstileErrorDetail(e: unknown): string | null {
  const s = (e instanceof Error ? e.message : typeof e === 'string' ? e : '').replace(/\s+/g, ' ').trim()
  return s ? s.slice(0, 160) : null
}

/** Mã lỗi từ `error-callback` → chuỗi ngắn an toàn để in ra màn hình / console (chỉ chữ, số, `.`, `_`, `-`, tối đa 32 ký tự). */
export function normalizeTurnstileErrorCode(code: unknown): string | null {
  if (typeof code !== 'string' && typeof code !== 'number') return null
  const s = String(code).trim()
  return /^[A-Za-z0-9._-]{1,32}$/.test(s) ? s : null
}

/** Câu thông báo (KHÔNG gồm nút "thử lại" — nơi hiển thị nối thêm). Có mã lỗi để người dùng đọc cho bộ phận hỗ trợ. */
export function turnstileFailureMessage(f: TurnstileFailure): string {
  if (f.kind === 'script') return i18n.t('sharedMisc:turnstile.failure.script')
  if (f.kind === 'render') return i18n.t('sharedMisc:turnstile.failure.render')
  return i18n.t('sharedMisc:turnstile.failure.widget', { code: f.code ? i18n.t('sharedMisc:turnstile.failure.codeSuffix', { code: f.code }) : '' })
}

/** Dòng ghi ra `console.warn` để chẩn đoán (Cloudflare không in lỗi vì `error-callback` trả true). Không chứa khoá hay token. */
export function turnstileFailureLog(f: TurnstileFailure, action: string): string {
  if (f.kind === 'script') return `[turnstile] không tải được api.js từ challenges.cloudflare.com (form: ${action}) — mạng / trình chặn quảng cáo / tường lửa`
  if (f.kind === 'render') return `[turnstile] render() ném lỗi, widget không được dựng (form: ${action}${f.detail ? `, chi tiết: ${f.detail}` : ''}) — kiểm tra khoá công khai (TURNSTILE_SITE_KEY)`
  return `[turnstile] widget báo lỗi (form: ${action}, mã lỗi: ${f.code ?? 'không có'}) — Cloudflare sẽ tự thử lại`
}

/** Body gửi lên: thêm `turnstileToken` khi có token (Turnstile tắt → body y nguyên). */
export function withTurnstileToken<T extends object>(body: T, token: string | null | undefined): T & { turnstileToken?: string } {
  return token ? { ...body, turnstileToken: token } : body
}

/** Màu widget theo giao diện của ứng dụng (lớp `dark` trên <html>), không theo hệ điều hành. */
export function turnstileTheme(): 'light' | 'dark' {
  return typeof document !== 'undefined' && document.documentElement.classList.contains('dark') ? 'dark' : 'light'
}
